import { useState, useCallback } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { openChat as openSupportChat } from '../../utils/chat';

interface ReturnProps {
  isAuthenticated: boolean;
  crispChatUrl: string;
  currentUrl: string;
  lastUpdated: string;
}

const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CreditCardIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MessageIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const LeafIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 2c1 2 2 4.5 2 8 0 5.5-4.78 10-10 10z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

const MapPinIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const XCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

export default function Return({ isAuthenticated, crispChatUrl, currentUrl, lastUpdated }: ReturnProps) {
  const [chatOpened, setChatOpened] = useState(false);

  const openChat = useCallback(() => {
    // Use the new utility function with fallback support
    openSupportChat('Hola, necesito ayuda con una consulta sobre devoluciones.');
    setChatOpened(true);
    setTimeout(() => setChatOpened(false), 2000);
  }, []);

  const pageTitle = 'Política de Devolución | Abasto Virtual Córdoba';
  const pageDescription = 'Conocé nuestra política de devolución para frutas y verduras frescas. Garantizamos la calidad de nuestros productos con devolución del dinero si no cumple tus expectativas.';

  return (
    <MainLayout>
      <Head title={pageTitle}>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="política devolución, garantía calidad, frutas verduras, abasto virtual córdoba, reembolso dinero" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="/appicons/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="/appicons/og-image.jpg" />
        <link rel="canonical" href={currentUrl} />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16 pt-8 md:pt-12">
        <div className="mx-auto max-w-3xl px-4">
          {/* Header */}
          <div className="mb-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100">
              <RefreshIcon className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">
              Política de Devolución
            </h1>
            <p className="text-lg text-gray-500">
              Garantizamos la calidad de nuestros productos frescos
            </p>
          </div>

          {/* Intro card */}
          <div className="mb-10 rounded-2xl border-l-4 border-green-500 bg-green-50 p-6">
            <div className="mb-2 flex items-center gap-2">
              <CheckIcon className="h-5 w-5 text-green-600" />
              <h3 className="font-bold text-green-800">Garantizamos la Calidad</h3>
            </div>
            <p className="text-base leading-relaxed text-green-900">
              En Abasto Virtual nos comprometemos con la frescura y calidad de nuestros productos.
              Si llega en mal estado, no es lo que pediste o no tiene la calidad esperada, lo resolvemos de una de estas tres formas: <strong>reposición</strong>, <strong>nota de crédito</strong> o <strong>reintegro al medio de pago</strong>.
            </p>
            <p className="mt-3 text-base leading-relaxed text-green-900">
              El detalle de <a href="/por-que-elegirnos" className="font-semibold underline">por qué no sustituimos</a> está en la página de por qué elegirnos.
            </p>
          </div>

          {/* Content sections */}
          <div className="space-y-10">
            {/* When to request */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                  <AlertIcon className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">¿Cuándo podés solicitar una devolución?</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                    <h4 className="font-semibold text-gray-900">Productos en Mal Estado</h4>
                  </div>
                  <p className="text-gray-600">
                    Frutas o verduras que lleguen deterioradas, podridas, o con signos evidentes de descomposición.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                    <h4 className="font-semibold text-gray-900">Calidad No Esperada</h4>
                  </div>
                  <p className="text-gray-600">
                    Productos que no cumplan con los estándares de calidad prometidos o que no coincidan con la descripción del pedido.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                    <h4 className="font-semibold text-gray-900">Producto Incorrecto</h4>
                  </div>
                  <p className="text-gray-600">
                    Si recibís un producto diferente al que solicitaste en tu pedido.
                  </p>
                </div>
              </div>
            </section>

            {/* Time limit */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                  <ClockIcon className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Tiempo Límite</h2>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
                <p className="text-base text-amber-900">
                  <strong>Debés reportar el problema inmediatamente</strong> al recibir tu pedido.
                  Dado que trabajamos con productos frescos, es importante que nos contactes <strong>dentro de las primeras 24 horas</strong> de recibida la entrega.
                </p>
              </div>
            </section>

            {/* Refund methods */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                  <CreditCardIcon className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Cómo lo resolvemos</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h4 className="mb-3 font-semibold text-green-700">1. Reposición</h4>
                  <p className="text-gray-600">
                    Te cambiamos el producto en una próxima entrega o en el próximo turno disponible.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h4 className="mb-3 font-semibold text-green-700">2. Nota de crédito</h4>
                  <p className="text-gray-600">
                    Te acreditamos el dinero en la cuenta para un próximo pedido.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h4 className="mb-3 font-semibold text-green-700">3. Reintegro al medio de pago</h4>
                  <p className="text-gray-600">
                    La plata vuelve al medio que usaste: MercadoPago, tarjeta o transferencia.
                    Si pagaste con MercadoPago, vuelve a MercadoPago. El plazo es de 5 a 10 días hábiles.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h4 className="mb-3 font-semibold text-green-700">Medios de pago</h4>
                  <p className="text-gray-600">
                    Podés pagar con MercadoPago, tarjeta, transferencia o efectivo.
                    El reintegro de dinero vuelve siempre al mismo medio cuando pagaste con MercadoPago, tarjeta o transferencia.
                  </p>
                </div>
              </div>
            </section>

            {/* How to request */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">¿Cómo solicitar una devolución?</h2>
              </div>

              <div className="space-y-4">
                {[
                  { step: 1, title: 'Contactános Inmediatamente', desc: 'Iniciá un chat en vivo con nuestro equipo y compartí fotos del producto junto con la descripción del problema.' },
                  { step: 2, title: 'Evaluación del Caso', desc: 'Nuestro equipo revisará tu reporte y confirmará la devolución.' },
                  { step: 3, title: 'Elegís cómo seguir', desc: 'Reposición, nota de crédito o reintegro al medio de pago que usaste.' },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="rounded-xl border-l-4 border-blue-400 bg-slate-50 p-5">
                    <div className="mb-2 flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                        {step}
                      </span>
                      <h4 className="font-semibold text-gray-900">{title}</h4>
                    </div>
                    <p className="ml-10 text-gray-600">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact / chat */}
            <section className="rounded-2xl bg-green-50 p-6">
              <div className="mb-4 flex items-center gap-2">
                <MessageIcon className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-bold text-green-900">¿Necesitás ayuda?</h3>
              </div>
              <p className="mb-5 text-green-800">
                Nuestro equipo de atención al cliente está disponible para ayudarte con cualquier consulta sobre devoluciones.
              </p>
              <button
                type="button"
                onClick={openChat}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold transition-all ${
                  chatOpened
                    ? 'bg-green-600 text-white'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {chatOpened ? (
                  <>
                    <CheckIcon className="h-5 w-5" />
                    Chat Abierto
                  </>
                ) : (
                  <>
                    <MessageIcon className="h-5 w-5" />
                    Iniciar Chat en Vivo
                  </>
                )}
              </button>
            </section>

            {/* Commitment */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                  <LeafIcon className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Nuestro Compromiso</h2>
              </div>

              <p className="text-base leading-relaxed text-gray-600">
                En Abasto Virtual trabajamos con productores locales para garantizar productos de la más alta calidad.
                Sin embargo, entendemos que los productos frescos pueden presentar variaciones naturales.
                <strong> Tu satisfacción es nuestra prioridad</strong>, por eso mantenemos esta política de devolución clara y justa.
              </p>
            </section>

            {/* Footer info */}
            <div className="space-y-4">
              <div className="rounded-xl bg-gray-100 p-5">
                <div className="mb-2 flex items-center gap-2">
                  <MapPinIcon className="h-5 w-5 text-gray-500" />
                  <h4 className="font-semibold text-gray-700">Información de Contacto</h4>
                </div>
                <p className="text-sm text-gray-600">
                  De La Semillería 1731 - Ampl. Palmar, Córdoba, 5012, Argentina
                </p>
              </div>

              <div className="rounded-xl bg-gray-50 py-4 text-center">
                <p className="text-sm text-gray-500">
                  Última actualización: {lastUpdated}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Política de Devolución',
            description: 'Política de devolución de Abasto Virtual para frutas y verduras frescas',
            url: currentUrl,
            mainEntity: {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'AR',
              returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
              merchantReturnDays: 1,
              returnMethod: 'https://schema.org/ReturnInStore',
              returnFees: 'https://schema.org/FreeReturn',
              merchant: {
                '@type': 'Organization',
                name: 'Abasto Virtual',
                url: 'https://www.abastovirtual.com',
              },
            },
          }),
        }}
      />
    </MainLayout>
  );
}
